import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBackOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useTranslation } from 'react-i18next';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../core/auth/useAuth';
import { colors } from '../core/theme/theme';
import { updateClient } from '../core/data/adminMutations';
import { useClientProjects } from '../features/clients/useClientProjects';
import { useClientDoc } from '../features/projects/useClientDoc';
import { useAllClientIds } from '../features/admin/useAllClientIds';
import ClientWizard, { type ClientFormData } from '../features/admin/ClientWizard';

/**
 * Read-only client profile page.
 *
 * - Accessible to any authenticated user (Firestore rule on `/clients` allows
 *   `read: if isAuthenticated()`).
 * - The "Modify" button is only rendered when the current user has the `admin`
 *   role. Even if a non-admin manages to trigger the wizard (e.g. by forging a
 *   DOM state), the Firestore rule on `clients/{id}` still blocks the write.
 * - Lists every project whose `clientId` matches this client. We use a live
 *   `onSnapshot` query so newly-created projects appear without a page refresh.
 */
export default function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  // Live subscription to the client doc.
  const { client, loading, error } = useClientDoc(id ?? '');

  // Live list of all projects for this client (server-side clientId filter under
  // REST; SDK where('clientId','==') otherwise). `open` is always true here — the
  // page is dedicated to this client, unlike the modal that also uses this hook.
  const {
    rows: projectRows,
    loading: projectsLoading,
    error: projectsErrorObj,
  } = useClientProjects(true, id ?? null);
  const projects = useMemo(
    () => projectRows.map((r) => ({ id: r.id, data: r.data })),
    [projectRows],
  );
  const projectsError = Boolean(projectsErrorObj);
  // Typically FAILED_PRECONDITION: missing composite index — make it
  // visible instead of rendering a misleading "no projects".
  useEffect(() => {
    if (projectsErrorObj) {
      console.error('ClientDetailPage: projects query failed', projectsErrorObj);
    }
  }, [projectsErrorObj]);

  const [editOpen, setEditOpen] = useState(false);
  // Admin-only: pull the whole clients list for the wizard's uniqueness
  // check. Not subscribed unless the admin opens the wizard.
  const allClients = useAllClientIds(editOpen);

  const isAdmin = user?.roles.includes('admin') ?? false;

  const handleSaveEdit = async (values: ClientFormData) => {
    if (!id) return;
    const foundedYearNum = values.foundedYear ? parseInt(values.foundedYear, 10) : undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: Record<string, any> = {
      name: values.name,
      description: values.description || null,
      logoURL: values.logoURL || null,
      website: values.website || null,
      linkedinURL: values.linkedinURL || null,
      industry: values.industry || null,
      companyType: values.companyType || null,
      employeeRange: values.employeeRange || null,
      country: values.country || null,
      city: values.city || null,
      foundedYear: foundedYearNum !== undefined && !isNaN(foundedYearNum) ? foundedYearNum : null,
      parentCompany: values.parentCompany || null,
      tags: values.tags.length > 0 ? values.tags : null,
      updatedAt: serverTimestamp(),
    };
    await updateClient(id, payload);
  };

  const locationLine = useMemo(() => {
    if (!client) return '';
    const parts = [client.city, client.country].filter(Boolean);
    return parts.join(', ');
  }, [client]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return <Alert severity="error">{error?.message ?? t('clients.detail.notFound')}</Alert>;
  }

  // Subtitle line: "Industry · City, Country"
  const subtitleParts = [client.industry, locationLine].filter(Boolean);
  const subtitle = subtitleParts.join(' · ');

  return (
    <Box>
      {/* Back button */}
      <Button
        variant="text"
        color="inherit"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/customers')}
        sx={{ color: colors.textMuted, mb: 2 }}
      >
        {t('clients.detail.backToList')}
      </Button>

      {/* ── 2-column layout ──────────────────────────────────────── */}
      <Grid container spacing={0}>
        {/* LEFT COLUMN — header + description + projects */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Merged header card: logo, name, subtitle, description, tags, links */}
          <Paper
            variant="outlined"
            sx={{ p: 3, borderColor: colors.lightGray, borderRight: { md: 'none' } }}
          >
            <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Avatar
                src={client.logoURL ?? undefined}
                variant="square"
                sx={{
                  width: 72,
                  height: 72,
                  fontSize: 24,
                  fontWeight: 700,
                  bgcolor: colors.lightGray,
                  color: colors.bodyText,
                  flexShrink: 0,
                }}
              >
                {getInitials(client.name)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="h2" sx={{ lineHeight: 1.15 }}>
                    {client.name}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {/* Link icons + edit */}
                  {client.website && (
                    <IconButton
                      component="a"
                      href={client.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('clients.detail.websiteAria')}
                      size="small"
                    >
                      <LanguageIcon fontSize="small" />
                    </IconButton>
                  )}
                  {client.linkedinURL && (
                    <IconButton
                      component="a"
                      href={client.linkedinURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('clients.detail.linkedinAria')}
                      size="small"
                    >
                      <LinkedInIcon fontSize="small" />
                    </IconButton>
                  )}
                  {isAdmin && (
                    <IconButton
                      onClick={() => setEditOpen(true)}
                      size="small"
                      aria-label={t('common.edit')}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  )}
                </Stack>

                {/* Subtitle: industry + location */}
                {subtitle && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {subtitle}
                  </Typography>
                )}

                {/* Description inline */}
                {client.description && (
                  <Typography variant="body2" sx={{ mt: 1, color: colors.bodyText }}>
                    {client.description}
                  </Typography>
                )}

                {/* Tags — moved from sidebar to header (useful for quick ID) */}
                {client.tags && client.tags.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                    {client.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* Associated projects — with status-colored tiles */}
          <Paper
            variant="outlined"
            sx={{ p: 2.5, borderColor: colors.lightGray, borderTop: 'none' }}
          >
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              {t('clients.detail.projectsTitle', { count: projects.length })}
            </Typography>
            {projectsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : projectsError ? (
              <Alert severity="error">{t('clients.detail.projectsError')}</Alert>
            ) : projects.length === 0 ? (
              <Muted>{t('clients.detail.noProjects')}</Muted>
            ) : (
              <Stack spacing={1}>
                {projects.map(({ id: pid, data }) => {
                  return (
                    <Paper
                      key={pid}
                      variant="outlined"
                      onClick={() => navigate(`/projects/${pid}`)}
                      sx={{
                        p: 1.5,
                        borderColor: colors.lightGray,
                        borderLeft: `4px solid ${colors.yellow}`,
                        cursor: 'pointer',
                        transition: 'border-color 150ms, background-color 150ms',
                        '&:hover': {
                          borderColor: colors.yellow,
                          bgcolor: colors.peach,
                        },
                      }}
                      data-testid={`client-project-row-${pid}`}
                    >
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                            {data.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {data.code} · {data.currentProgram}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        {/* RIGHT COLUMN — company info on warm tinted surface */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box
            sx={{
              bgcolor: 'rgba(237, 200, 163, 0.15)',
              borderTop: { xs: `1px solid ${colors.lightGray}`, md: 'none' },
              border: { md: `1px solid ${colors.lightGray}` },
              borderLeft: { md: 'none' },
              p: 2.5,
              height: '100%',
            }}
          >
            <Typography variant="h4" sx={{ mb: 1.5 }}>
              {t('clients.detail.companyInfo')}
            </Typography>
            <DetailRow label={t('admin.clients.industry')}>
              {client.industry || <Muted>—</Muted>}
            </DetailRow>
            <DetailRow label={t('admin.clients.companyType')}>
              {client.companyType
                ? t(`admin.clients.companyTypes.${client.companyType}`)
                : <Muted>—</Muted>}
            </DetailRow>
            <DetailRow label={t('admin.clients.employeeRange')}>
              {client.employeeRange || <Muted>—</Muted>}
            </DetailRow>
            <DetailRow label={t('admin.clients.foundedYear')}>
              {client.foundedYear ?? <Muted>—</Muted>}
            </DetailRow>
            <DetailRow label={t('admin.clients.parentCompany')}>
              {client.parentCompany || <Muted>—</Muted>}
            </DetailRow>
          </Box>
        </Grid>
      </Grid>

      {/* Edit wizard (admin only) */}
      {isAdmin && (
        <ClientWizard
          open={editOpen}
          editing={{ id: id!, data: client }}
          allClients={allClients}
          onSubmit={handleSaveEdit}
          onClose={() => setEditOpen(false)}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        py: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="div"
        sx={{
          maxWidth: '65%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'right',
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="span" variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}


function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}
